import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RotateCcw, ChevronRight, Loader2, Search } from 'lucide-react';
import { fetchAdminRMAs, closeAdminRMA } from '../../services/rmaService';
import { formatErpRmaNumber } from '../../types';
import { RMARequest } from '../../types';
import { toast } from 'sonner';
import { ConfirmActionDialog } from '../../components/admin/ConfirmActionDialog';
import { LoadError } from '../../components/rma/LoadError';
import { numeroDocumento } from '../../utils/numeroDocumento';

// ─── Status config ─────────────────────────────────────────────────────────────
const STATUS_MAP: Record<string, { label: string; color: string }> = {
  aperta:         { label: 'Aperta',                 color: 'bg-blue-100 text-blue-700' },
  in_lavorazione: { label: 'In Lavorazione',         color: 'bg-amber-100 text-amber-700' },
  chiusa:         { label: 'Chiusa',                 color: 'bg-gray-100 text-gray-600' },
};

// ─── Componente ────────────────────────────────────────────────────────────────
// Nota: la valutazione pezzo per pezzo (approva/respingi/nota/allegato) è
// stata spostata nella pagina di dettaglio dedicata (AdminRMADetailPage,
// route /admin/rma/:numero) — qui resta solo l'elenco riassuntivo.
export default function AdminRMAPage() {
  const navigate = useNavigate();
  const [rmas, setRmas] = useState<RMARequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  // Chiusura: conferma esplicita e un solo invio alla volta (audit RMA #18).
  const [confirmCloseId, setConfirmCloseId] = useState<string | null>(null);
  const [closingId, setClosingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    fetchAdminRMAs()
      .then(items => { if (!cancelled) setRmas(items); })
      .catch(() => { if (!cancelled) setLoadError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [attempt]);

  const closePractice = async (rmaId: string) => {
    if (closingId) return;
    setClosingId(rmaId);
    try {
      const updated = await closeAdminRMA(rmaId);
      setRmas(prev => prev.map(r => (r.id === rmaId ? updated : r)));
      toast.success(`RMA ${rmaId} chiusa.`);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Errore durante la chiusura della pratica.');
    } finally {
      setClosingId(null);
    }
  };

  // ─── Filtri ─────────────────────────────────────────────────────────────────
  const FILTER_OPTIONS = [
    { key: 'all',            label: 'Tutte' },
    { key: 'aperta',         label: 'Aperte' },
    { key: 'in_lavorazione', label: 'In Lavorazione' },
    { key: 'chiusa',         label: 'Chiuse' },
  ];

  const countByStatus = rmas.reduce<Record<string, number>>((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  const normalizedSearch = search.trim().toLowerCase();
  // Senza punteggiatura: "satmu" trova "S.A.T.M.U. 2000 S.R.L.".
  const compatta = (v: string) => v.toLowerCase().replace(/[^a-z0-9]/g, '');
  const compactSearch = compatta(normalizedSearch);
  const filteredRmas = useMemo(() => {
    return rmas.filter(r => {
      if (filter !== 'all' && r.status !== filter) return false;
      if (!normalizedSearch) return true;
      const erpNumero = formatErpRmaNumber(r.erpRma) || '';
      return (
        r.id.toLowerCase().includes(normalizedSearch) ||
        (r.numero || '').toLowerCase().includes(normalizedSearch) ||
        erpNumero.toLowerCase().includes(normalizedSearch) ||
        (!!compactSearch && compatta((r as any).ragioneSocialeCliente || '').includes(compactSearch)) ||
        String((r as any).anConto || '').includes(normalizedSearch) ||
        numeroDocumento(r.orderId).toLowerCase().includes(normalizedSearch) ||
        (r.items || []).some((i: any) => (i.sku || '').toLowerCase().includes(normalizedSearch))
      );
    });
  }, [rmas, filter, normalizedSearch]);

  // ─── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center text-muted-foreground" data-testid="admin-rma-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento pratiche RMA...
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6" data-testid="admin-rma-page">
      {/* Intestazione */}
      <div className="flex items-center gap-3 mb-6">
        <RotateCcw className="w-5 h-5 text-muted-foreground" />
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Gestione RMA</h1>
        <span className="text-xs px-2 py-0.5 rounded bg-secondary text-muted-foreground">
          {rmas.length} pratiche
        </span>
      </div>

      {/* Filtro testuale + filtri di stato */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per RMA, cliente, documento o articolo…"
            data-testid="rma-search"
            className="w-full h-9 pl-9 pr-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)]/30"
          />
        </div>
        <span className="text-xs text-muted-foreground" data-testid="rma-counter">
          {filteredRmas.length} {filteredRmas.length === 1 ? 'risultato' : 'risultati'}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {FILTER_OPTIONS.map(f => {
          const count = f.key === 'all' ? rmas.length : countByStatus[f.key] ?? 0;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`h-7 px-3 rounded-full text-xs font-medium transition-colors ${
                filter === f.key
                  ? 'bg-[hsl(214,58%,41%)] text-white'
                  : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
              }`}
              data-testid={`rma-filter-${f.key}`}
            >
              {f.label}
              {count > 0 && <span className="ml-1.5 opacity-70">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Tabella pratiche — stesso pattern della sezione Clienti (AdminClientsPage) */}
      <div className="bg-white rounded-xl border overflow-hidden">
        {loadError ? (
          <div className="p-4">
            <LoadError message="Non è stato possibile caricare le pratiche RMA." onRetry={() => setAttempt(a => a + 1)} testId="admin-rma-error" />
          </div>
        ) : filteredRmas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground" data-testid="admin-rma-empty">
            <RotateCcw className="w-10 h-10 mb-3 opacity-30" />
            <p className="font-medium text-sm">Nessuna pratica RMA trovata.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" style={{ minWidth: 1100 }}>
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky left-0 z-20 bg-gray-50">Codice RMA</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">Stato</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">Cliente</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">Documento</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">RMA Gestionale</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">Data</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">Pezzi</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">In attesa</th>
                  <th className="text-right px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide sticky right-0 z-20 bg-gray-50" />
                </tr>
              </thead>
              <tbody>
                {filteredRmas.map(rma => {
                  const allUnits = rma.items.flatMap(i => i.units ?? []);
                  const st = STATUS_MAP[rma.status] ?? STATUS_MAP.aperta;
                  const pendingN = allUnits.filter(u => (u.adminDecision ?? 'pending') === 'pending').length;
                  const canClose = rma.status !== 'chiusa' && (allUnits.length === 0 || pendingN === 0);
                  const erpNumero = formatErpRmaNumber(rma.erpRma);

                  return (
                    <tr
                      key={rma.id}
                      className="border-b last:border-b-0 hover:bg-gray-50/60 transition-colors cursor-pointer group"
                      onClick={() => navigate(`/admin/rma/${rma.id}`)}
                      data-testid={`admin-rma-row-${rma.id}`}
                    >
                      <td className="px-4 py-3 text-sm font-mono font-bold text-[hsl(214,58%,41%)] whitespace-nowrap sticky left-0 z-10 bg-white group-hover:bg-gray-50">
                        {rma.id}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${st.color}`}>
                          {st.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{rma.ragioneSocialeCliente || '—'}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{numeroDocumento(rma.orderId)}</td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap" data-testid={`admin-rma-erp-numero-${rma.id}`}>
                        {erpNumero ? <span className="font-mono font-semibold">{erpNumero}</span> : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{rma.date}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{allUnits.length || rma.items.length}</td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        {pendingN > 0 && rma.status !== 'chiusa' ? (
                          <span className="text-amber-600 font-medium">{pendingN}</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap sticky right-0 z-10 bg-white shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.18)]">
                        <div className="flex items-center justify-end gap-2">
                          {canClose && (
                            <button
                              onClick={e => { e.stopPropagation(); setConfirmCloseId(rma.id); }}
                              disabled={closingId !== null}
                              className="h-7 px-3 rounded-lg border text-xs font-medium hover:bg-secondary/60 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                              data-testid={`rma-close-${rma.id}`}
                            >
                              {closingId === rma.id && <Loader2 className="w-3 h-3 animate-spin" />}
                              {closingId === rma.id ? 'Chiusura…' : 'Chiudi Pratica'}
                            </button>
                          )}
                          <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ConfirmActionDialog
        open={confirmCloseId !== null}
        onOpenChange={open => { if (!open) setConfirmCloseId(null); }}
        variant="warning"
        title={`Chiudere la pratica ${confirmCloseId ?? ''}?`}
        description="Se ci sono pezzi approvati verrà creato l'Ordine RMA sul gestionale. La pratica non sarà più modificabile."
        confirmLabel="Chiudi pratica"
        onConfirm={() => { if (confirmCloseId) closePractice(confirmCloseId); }}
      />
    </div>
  );
}
